import {useEffect, useState} from 'react'
import RouteTitle from "../../../common/RouteTitle/RouteTitle"
import booksitoutIcon from "../../../config/BooksitoutIcon"
import BooksitoutIcon from "../../../config/BooksitoutIcon"
import RouteTitleConfig from "../../../config/RouteTitleConfig"
import RouteContainer from "../../../common/styles/RouteContainer"
import RowSpacer from "../../../common/styles/RowSpacer"
import NoContentForFullScreen from "../../../common/NoContentForFullScreen"
import NoContent from "../../../common/NoContent"
import OAuthConfig from "../../../config/OAuthConfig"
import useAppUserSettingsPersonalInfo from "./useAppUserSettingsPersonalInfo"
import styled from "styled-components"
import {Card, CardTitle} from "react-bootstrap"
import CardBodyContentContainer from "../../../common/styles/CardBodyContentContainer"
import useLoginStore from "../../login/useLoginStore"
import {useNavigate} from "react-router-dom"
import useHtmlTitle from "../../../common/useHtmlTitle"
import {useDarkMode} from "../../../common/hooks/DarkModeContext";
import {isPasskeySupported, registerPasskey} from "../../../utils/passkey";
import toast from "react-hot-toast";
import {BooksitoutServer} from "../../../config/BooksitoutServer";
import LoadingBar from "../../../common/LoadingBar";
import BooksitoutImages from "../../../images/BooksitoutImages";

interface LoginSession {
    id: number
    platform: string
    oauthProvider: string
    deviceDescription: string
    loginTime: string
    lastActiveTime: string | null
}

const PLATFORM_LABEL: Record<string, string> = { IOS: 'iPhone/iPad 앱', WEB: '웹사이트', TOSS: '토스' }
const PROVIDER_LABEL: Record<string, string> = { KAKAO: '카카오', NAVER: '네이버', GOOGLE: '구글', APPLE: 'Apple', TOSS: '토스', NOT_USING: '-' }
const getProviderIcon = (provider: string, isDark: boolean): string | null => {
    const icons: Record<string, string> = {
        KAKAO: BooksitoutImages.LoginButton.kakao,
        NAVER: BooksitoutImages.LoginButton.naver,
        GOOGLE: BooksitoutImages.LoginButton.google,
        APPLE: isDark ? BooksitoutImages.LoginButton.appleDark : BooksitoutImages.LoginButton.apple,
        TOSS: BooksitoutImages.WebsiteLogo.toss,
    }
    return icons[provider] ?? null
}

const formatLoginTime = (dateStr: string) => {
    const d = new Date(dateStr)
    return `${String(d.getFullYear()).slice(-2)}년 ${String(d.getMonth() + 1).padStart(2, '0')}월 ${String(d.getDate()).padStart(2, '0')}일 ${String(d.getHours()).padStart(2, '0')}시 ${String(d.getMinutes()).padStart(2, '0')}분`
}

const SettingsPersonalInfoRoute = () => {
    useHtmlTitle('계정 설정')
    const [info, isLoading, isError] = useAppUserSettingsPersonalInfo()
    const [isPasskeyLoading, setIsPasskeyLoading] = useState(false)
    const [sessions, setSessions] = useState<LoginSession[]>([])
    const [isSessionsLoading, setIsSessionsLoading] = useState(true)

    useEffect(() => {
        BooksitoutServer.get('/v1/login-sessions')
            .then(res => {
                const sorted = (res.data as LoginSession[]).sort((a, b) => {
                    const timeA = a.lastActiveTime ?? a.loginTime
                    const timeB = b.lastActiveTime ?? b.loginTime
                    return new Date(timeB).getTime() - new Date(timeA).getTime()
                })
                setSessions(sorted)
            })
            .finally(() => setIsSessionsLoading(false))
    }, [])

    const onRevokeSession = (sessionId: number) => {
        if (!window.confirm('이 기기에서 로그아웃 할까요?')) return
        BooksitoutServer.delete(`/v1/login-sessions/${sessionId}`)
            .then(() => {
                toast.success('로그아웃 되었어요.')
                setSessions(prev => prev.filter(s => s.id !== sessionId))
            })
            .catch(() => toast.error('오류가 났어요. 잠시 후 다시 시도해 주세요.'))
    }

    const {isDark} = useDarkMode()

    const navigate = useNavigate()
    const {isLoggedIn} = useLoginStore()
    useEffect(() => {
        if (!isLoggedIn()) {
            navigate(`/`)
        }
    }, []);

    const handleAddPasskey = async () => {
        if (!isPasskeySupported()) {
            toast.error('이 기기에서는 Passkey를 지원하지 않아요.')
            return
        }

        setIsPasskeyLoading(true)
        try {
            const result = await registerPasskey()
            if (result.success) {
                toast.success('Passkey가 등록됐어요!')
            } else {
                toast.error(result.error || 'Passkey 등록할 수 없었어요. 잠시 후 다시 시도해 주세요.')
            }
        } catch (error) {
            toast.error('Passkey 등록할 수 없었어요. 잠시 후 다시 시도해 주세요.')
        } finally {
            setIsPasskeyLoading(false)
        }
    }

    return (
        <RouteContainer>
            <RouteTitle
                icon={<booksitoutIcon.settings/>}
                title={'계정 설정'}
                currentKey={'PERSONAL'}
                buttons={RouteTitleConfig.Settings}
            />
            <RowSpacer/>

            {
                isLoading ? <></> :
                    isError ? <NoContentForFullScreen message={'오류가 났어요'}/> :
                        <Container>
                            <Card>
                                <CardBodyContentContainer>
                                    <CardTitle>로그인한 기기 관리</CardTitle>
                                    <LoginSessionDescription>현재 로그인된 기기를 확인하고, 필요하면 로그아웃 할 수 있어요.</LoginSessionDescription>
                                    <RowSpacer size={10}/>

                                    {isSessionsLoading ? (
                                        <SessionList>
                                            {Array.from({ length: 3 }).map((_, i) => (
                                                <SessionItem key={i}>
                                                    <SessionInfo>
                                                        <SessionRow><SessionLabel>서비스</SessionLabel><SessionValue><LoadingBar size={3} /></SessionValue></SessionRow>
                                                        <SessionRow><SessionLabel>로그인 방법</SessionLabel><SessionValue><LoadingBar size={2} /></SessionValue></SessionRow>
                                                        <SessionRow><SessionLabel>기기</SessionLabel><SessionValue><LoadingBar size={5} /></SessionValue></SessionRow>
                                                        <SessionRow><SessionLabel>로그인 시간</SessionLabel><SessionValue><LoadingBar size={6} /></SessionValue></SessionRow>
                                                        <SessionRow><SessionLabel>마지막 사용</SessionLabel><SessionValue><LoadingBar size={6} /></SessionValue></SessionRow>
                                                    </SessionInfo>
                                                    <RevokeButton disabled style={{ opacity: 0.3 }}>로그인 해제</RevokeButton>
                                                </SessionItem>
                                            ))}
                                        </SessionList>
                                    ) : sessions.length === 0 ? (
                                        <NoContent message={'로그인한 기기가 없어요'} />
                                    ) : (
                                        <SessionList>
                                            {sessions.map(session => (
                                                <SessionItem key={session.id}>
                                                    <SessionInfo>
                                                        <SessionRow>
                                                            <SessionLabel>서비스</SessionLabel>
                                                            <SessionValue>{PLATFORM_LABEL[session.platform] ?? session.platform}</SessionValue>
                                                        </SessionRow>
                                                        <SessionRow>
                                                            <SessionLabel>로그인 방법</SessionLabel>
                                                            <SessionValue>
                                                                {getProviderIcon(session.oauthProvider, isDark) && <ProviderIcon src={getProviderIcon(session.oauthProvider, isDark)!} alt="" />}
                                                                {PROVIDER_LABEL[session.oauthProvider] ?? session.oauthProvider}
                                                            </SessionValue>
                                                        </SessionRow>
                                                        <SessionRow>
                                                            <SessionLabel>기기</SessionLabel>
                                                            <SessionValue>{session.deviceDescription || '-'}</SessionValue>
                                                        </SessionRow>
                                                        <SessionRow>
                                                            <SessionLabel>로그인 시간</SessionLabel>
                                                            <SessionValue>{session.loginTime ? formatLoginTime(session.loginTime) : '-'}</SessionValue>
                                                        </SessionRow>
                                                        <SessionRow>
                                                            <SessionLabel>마지막 사용</SessionLabel>
                                                            <SessionValue>{session.lastActiveTime ? formatLoginTime(session.lastActiveTime) : '-'}</SessionValue>
                                                        </SessionRow>
                                                    </SessionInfo>
                                                    <RevokeButton onClick={() => onRevokeSession(session.id)}>로그인 해제</RevokeButton>
                                                </SessionItem>
                                            ))}
                                        </SessionList>
                                    )}
                                </CardBodyContentContainer>
                            </Card>
                            <RowSpacer/>

                            <Card style={{minHeight: '200px'}}>
                                <CardBodyContentContainer>
                                    <CardTitle>연결된 이메일</CardTitle>
                                    <RowSpacer size={10}/>

                                    <ul>
                                        {
                                            info?.emails.map(email =>
                                                <li>{email}</li>
                                            )
                                        }
                                    </ul>
                                </CardBodyContentContainer>
                            </Card>
                            <RowSpacer/>

                            <Card>
                                <CardBodyContentContainer>
                                    <CardTitle>계정 추가로 연결하기</CardTitle>
                                    <RowSpacer/>

                                    <OAuthContainer>
                                        {
                                            OAuthConfig.map(oauth =>
                                                <>
                                                    {
                                                        isOAuthAdded(oauth.id) &&
                                                        <OAuthAdded/>
                                                    }

                                                    <a href={oauth.addToExistingUrl}
                                                       style={{pointerEvents: (!oauth.active || isOAuthAdded(oauth.id)) ? 'none' : 'auto'}}>
                                                        <OAuthButton src={isDark ? oauth.imageDark : oauth.image} alt=""
                                                                     active={oauth.active && !isOAuthAdded(oauth.id)}/>
                                                    </a>
                                                </>
                                            )
                                        }
                                    </OAuthContainer>
                                    <RowSpacer/>
                                </CardBodyContentContainer>
                            </Card>
                            <RowSpacer/>

                            <Card>
                                <CardBodyContentContainer>
                                    <CardTitle>Passkey 추가하기</CardTitle>
                                    <RowSpacer/>

                                    <PasskeyContainer>
                                        <PasskeyButton
                                            onClick={handleAddPasskey}
                                            disabled={isPasskeyLoading || !isPasskeySupported() || info?.isPasskeyAdded == true}
                                        >
                                            {info?.isPasskeyAdded == true ? (
                                                <>✅ Passkey 이미 추가됨</>
                                            ) : isPasskeyLoading ? (
                                                <>Passkey를 추가하는 중...</>
                                            ) : isPasskeySupported() ? (
                                                <>Passkey 추가하기</>
                                            ) : (
                                                <>Passkey 미지원</>
                                            )}
                                        </PasskeyButton>

                                        {!isPasskeySupported() && (
                                            <PasskeyDescription>
                                                이 기기에서는 Passkey를 지원하지 않습니다.
                                            </PasskeyDescription>
                                        )}

                                        {isPasskeySupported() && (
                                            <PasskeyDescription>
                                                Passkey를 추가하면 비밀번호 없이 안전하게 로그인할 수 있어요.
                                            </PasskeyDescription>
                                        )}
                                    </PasskeyContainer>
                                    <RowSpacer/>
                                </CardBodyContentContainer>
                            </Card>
                            <RowSpacer/>
                        </Container>
            }
        </RouteContainer>
    )

    function isOAuthAdded(oauthId: number) {
        return (info?.isKakaoAdded && oauthId === 3) ||
            (info?.isNaverAdded && oauthId === 4) ||
            (info?.isGoogleAdded && oauthId === 1) ||
            (info?.isAppleAdded && oauthId === 5)
    }
}

const Container = styled.div``

const OAuthContainer = styled.div`
    text-align: center;
`;

interface OAuthButtonProps {
    active: boolean
}

const OAuthButton = styled.img.attrs((props) => ({
    // @ts-ignore
    className: `img-fluid ms-2 me-2 ms-md-3 me-md-3 rounded ${(!props.active && 'opacity-50')} hover-effect`
}))<OAuthButtonProps>`
    width: 50px;
`;

const OAuthAdded = styled(BooksitoutIcon.check)`
    font-size: 1.5rem;
    position: absolute;
    color: var(--primary-color);
`

const PasskeyContainer = styled.div`
    text-align: center;
`

const PasskeyButton = styled.button`
    color: var(--text-color);
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    margin: 10px 0;
    min-width: 200px;

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }
`

const PasskeyDescription = styled.p`
    color: var(--sub-text-color);
    font-size: 14px;
    margin-top: 10px;
    text-align: center;
`

const LoginSessionDescription = styled.p`
    color: var(--sub-text-color);
    margin-bottom: 0;
    font-size: 14px;
`

const SessionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const SessionItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-radius: 12px;
    background-color: var(--card-card-bg-color);

    @media (max-width: 576px) {
        flex-direction: column;
        align-items: stretch;
        gap: 0;
    }
`

const SessionInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const SessionRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
`

const SessionLabel = styled.span`
    color: var(--sub-text-color);
    font-size: 13px;
    min-width: 80px;
`

const SessionValue = styled.span`
    color: var(--text-color);
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 6px;
`

const ProviderIcon = styled.img`
    width: 18px;
    height: 18px;
    border-radius: 4px;
`

const RevokeButton = styled.button`
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    background-color: var(--danger);
    color: white;
    font-size: 14px;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        opacity: 0.9;
    }

    @media (max-width: 576px) {
        margin-top: 20px;
    }
`

export default SettingsPersonalInfoRoute
